import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Button, ButtonToolbar, Checkbox, Form, SelectPicker } from "rsuite";

import "./ChartFilters.css";
import { updateIn } from "json-immut";
import { HelperServices } from "../../../core/services/HelperServices";
import { get } from "lodash/fp";
import { ChartListParams } from "./ChartDataTypes";
import {
  chartAvailabilityObjArr,
  messageTypeArr,
  roleTypeArr,
  statusObjArr,
} from "../../../core/config/AppConstants";
import { useTranslation } from "react-i18next";
import { UtilityServices } from "../../../core/services/UtilityServices";
import { StoreServices } from "../../../core/services/StoreServices";

export interface PropTypesInterface {
  onSubmit: Function;
  onClear: Function;
  tabViewSelected?: String;
}

export default function ChartFilters({
  onSubmit,
  onClear,
  tabViewSelected,
}: PropTypesInterface) {
  const { t } = useTranslation();
  const initSearchFormData = {
    name: "",
    company: "",
    store_group: "",
    event: "",
    license: "",
    company_type: "",
    for: "",
    for_all_company: "",
    type: "",
    availability: "",
    start_date: "",
    end_date: "",
    status: "",
    smart_search: "",
  };
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<ChartListParams>(initSearchFormData);
  const company = useSelector((state: any) => state.company);
  const [storeGroupList, setStoreGroupList] = useState([]);
  const [licenseList, setLicenseList] = useState([]);
  const [eventList, setEventList] = useState([]);
  const [compTypeList, setCompTypeList] = useState([]);
  const [showFilter, setShowFilter] = useState(false);
  const handleSearchToggle = () => setShowFilter(!showFilter);

  function handleFormChanges(data: any) {
    let form = updateIn(formData, [data.path], data.value);
    setFormData(form);
  }

  async function init() {
    try {
      getLicenseList({});
      getEventList({});
      getCompTypeList({});
    } catch (error) {
      console.log(error);
    }
  }

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {}, [tabViewSelected]);

  useEffect(() => {
    storeGroupSearch("", company.id);
    getLicenseList({ company: company.id });
  }, [company.id]);

  async function getCompTypeList(params: any) {
    try {
      setLoading(true);
      const res = await HelperServices.getCompanyTypes(params);
      setCompTypeList(get("data.data", res));
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }

  async function getEventList(params: any) {
    try {
      setLoading(true);
      const res = await HelperServices.getEvents(params);
      setEventList(get("data.data", res));
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }

  async function getLicenseList(params: any) {
    try {
      let compId: number = getCompanyId();

      if (compId) {
        params = { ...params, ...{ company: compId } };
      }
      setLoading(true);
      const res = await HelperServices.getAllValidLicenses(params);
      setLicenseList(get("data.data", res));
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  }

  async function storeGroupSearch(searchGroup?: string, forComp?: number) {
    let compId: number = getCompanyId(forComp);
    setStoreGroupList([]);
    if (!compId) {
      return;
    }
    let params: any = { smart_search: searchGroup };
    if (compId) {
      params.company = compId;
    }
    try {
      const res = await StoreServices.getStoreGroups(params);
      const respData = get("data.data", res);
      if (respData.length) {
        setStoreGroupList(respData);
      }
    } catch (error: any) {
      UtilityServices.showToaster({
        message: error.message,
        type: messageTypeArr.error,
      });
    }
  }

  function getCompanyId(forComp?: number) {
    let compId: number = 0;
    if (UtilityServices.getLoggedInUser("user_type") == roleTypeArr.client) {
      compId = UtilityServices.getLoggedInUser("company_id");
    } else if (
      UtilityServices.getLoggedInUser("user_type") == roleTypeArr.backend
    ) {
      compId = forComp ? forComp : company.id;
    }
    return compId;
  }

  function clearSearch() {
    onClear();
    setFormData(initSearchFormData);
  }

  const handleForAllCompCheck = (value: any, checked: any) => {
    let form = updateIn(formData, ["for_all_company"], checked ? value : "");
    setFormData(form);
  };

  const handleEventCheck = (value: any, checked: any) => {
    let form = updateIn(formData, ["type"], checked ? value : "");
    setFormData(form);
  };

  function renderTextField(
    controlId: string,
    label: string,
    name: keyof ChartListParams
  ) {
    return (
      <div className="chart-filter-field" key={controlId}>
        <Form.Group controlId={controlId}>
          <Form.ControlLabel>{label}</Form.ControlLabel>
          <Form.Control name={name} />
        </Form.Group>
      </div>
    );
  }

  function renderSelectField({
    controlId,
    label,
    name,
    data,
    valueKey,
    labelKey,
    onSelect,
    onSearch,
    onOpen,
  }: {
    controlId: string;
    label: string;
    name: keyof ChartListParams;
    data: any[];
    valueKey: string;
    labelKey: string;
    onSelect: (value: any) => void;
    onSearch?: (value: string) => void;
    onOpen?: () => void;
  }) {
    return (
      <div className="chart-filter-field" key={controlId}>
        <Form.Group controlId={controlId}>
          <Form.ControlLabel>{label}</Form.ControlLabel>
          <SelectPicker
            data={data}
            valueKey={valueKey}
            labelKey={labelKey}
            onOpen={onOpen}
            onSearch={onSearch}
            onSelect={(value) => onSelect(value)}
            onClean={() => onSelect("")}
            name={name}
            value={formData?.[name]}
            cleanable
            block
            searchable
            placement="autoVerticalStart"
          />
        </Form.Group>
      </div>
    );
  }

  function renderCheckboxField({
    controlId,
    label,
    name,
    value,
    checked,
    onChange,
  }: {
    controlId: string;
    label: string;
    name: string;
    value: any;
    checked: boolean;
    onChange: (value: any, checked: any) => void;
  }) {
    return (
      <div className="chart-filter-field chart-filter-checkbox" key={controlId}>
        <Form.Group controlId={controlId}>
          <Form.ControlLabel>{label}</Form.ControlLabel>
          <Checkbox
            name={name}
            value={value}
            onChange={onChange}
            checked={checked}
          />
        </Form.Group>
      </div>
    );
  }

  return (
    <div>
      <div className="row mb-2">
        <ButtonToolbar className="mb-hide">
          <Button onClick={handleSearchToggle}>
            {t("CHART")} {t("ADVANCE_SEARCH")}
          </Button>
        </ButtonToolbar>
      </div>
      {showFilter && (
        <div className="content mb-3 pb-0 chart-filter-panel">
          <Form
            formValue={formData}
            onChange={(data: any) => {
              let form = { ...formData, ...data };
              setFormData(form);
            }}
            fluid
            className="chart-filter-form"
          >
            <section className="chart-filter-section">
              <div className="chart-filter-grid">
                {renderTextField("name", t("NAME"), "name")}
                {renderSelectField({
                  controlId: "store_group",
                  label: `${t("STORE")} ${t("GROUP")}`,
                  name: "store_group",
                  data: storeGroupList,
                  valueKey: "id",
                  labelKey: "region_group_name",
                  onOpen: () => storeGroupSearch(""),
                  onSelect: (value) =>
                    handleFormChanges({ path: "store_group", value }),
                })}
                {renderSelectField({
                  controlId: "status",
                  label: t("STATUS"),
                  name: "status",
                  data: statusObjArr,
                  valueKey: "value",
                  labelKey: "label",
                  onSelect: (value) =>
                    handleFormChanges({ path: "status", value }),
                })}
                {renderSelectField({
                  controlId: "availability",
                  label: t("AVAILABILITY"),
                  name: "availability",
                  data: chartAvailabilityObjArr,
                  valueKey: "value",
                  labelKey: "label",
                  onSelect: (value) =>
                    handleFormChanges({ path: "availability", value }),
                })}
                {renderCheckboxField({
                  controlId: "type",
                  label: `${t("EVENT")} ${t("TYPE")}`,
                  name: "type",
                  value: "event",
                  onChange: handleEventCheck,
                  checked: formData.type == "event",
                })}
                {renderSelectField({
                  controlId: "event",
                  label: t("EVENT"),
                  name: "event",
                  data: eventList,
                  valueKey: "id",
                  labelKey: "event_name",
                  onSelect: (value) =>
                    handleFormChanges({ path: "event", value }),
                })}
                {renderSelectField({
                  controlId: "license",
                  label: t("LICENSE"),
                  name: "license",
                  data: licenseList,
                  valueKey: "id",
                  labelKey: "license_name",
                  onSelect: (value) =>
                    handleFormChanges({ path: "license", value }),
                })}
                {renderCheckboxField({
                  controlId: "for_all_company",
                  label: `${t("FOR")} ${t("ALL")} ${t("COMPANY")}`,
                  name: "for_all_company",
                  value: 1,
                  onChange: handleForAllCompCheck,
                  checked: formData.for_all_company != "",
                })}
                {renderSelectField({
                  controlId: "company_type",
                  label: `${t("INDUSTRY")} ${t("TYPE")}`,
                  name: "company_type",
                  data: compTypeList,
                  valueKey: "id",
                  labelKey: "type_name",
                  onSelect: (value) =>
                    handleFormChanges({ path: "company_type", value }),
                })}
              </div>
            </section>
            <div className="filter-btn chart-filter-actions">
              <Button appearance="subtle" onClick={() => clearSearch()}>
                {t("CLEAR_FILTER")}
              </Button>
              <Button appearance="primary" onClick={() => onSubmit(formData)}>
                {t("SEARCH")}
              </Button>
            </div>
          </Form>
        </div>
      )}
    </div>
  );
}
